import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React, { ReactElement, useContext, useEffect, useState } from "react";

import { createOrder, getCreditCards, getPaymentChannels, getPaymentErrorInfos } from "@/service/rapyd.service";
import { OrderParams, PaymentMethodsServerKeys, ResponseChannel, CompanyCodes } from "@/interfaces/rapydTypes";
import LoadingWidget from "@/components/GlobalLoading/Loading";

import { useRouter } from "next/router";
import rfStore from "@/utils/rfStore";
import clientDeepLinks, { ClientHeadBtnTypes } from "@/utils/clientDeepLinks";

import DepositLayout from "@/layout/DepositLayout";
import PaymentCard from "@/containers/deposit/PaymentCard";
import DepositContext from "@/utils/depositContext";
import { i18n, useTranslation } from "next-i18next";
import { paymentMethodSortRules } from "@/interfaces/constants";

import ServiceProvider from "@/containers/common/ServiceProvider";
import DepositContainer from "@/containers/deposit/DepositContainer";
import Toast from "@/components/Toast";
import log2Sentry from "@/utils/log2Sentry";
import { DepositError } from "@/utils/sentryError";
import handleDeepLink from "@/utils/handleDeepLink";
import NoticeModalBottom from "@/containers/common/NoticeModalBottom";

interface RapydPaymentProps {
	visibleCount?: string;
	orderId?: string;
	channelData: ResponseChannel[];
	_price: number;
	_currency: string;
	_region: string;
}

let tryErrorCount = 1;

function transferChannelData(channelData: ResponseChannel[], currency: string, price: number) {
	if (!channelData || channelData.length === 0) return [];
	if (!currency || !price) return [];
	// 进行排序
	let _data = [...channelData];
	const upperCurrency = currency.toLocaleUpperCase();
	// 是新币 - 排除 电汇以及港币本地转账
	if (upperCurrency === "SGD") {
		_data = _data.filter((item) => item.channelName !== PaymentMethodsServerKeys.WIRE_TRANSFER);
	}
	// 是美元 | 港币 - 排除 SGD本地转账
	if (["USD", "HKD"].includes(upperCurrency)) {
		_data = _data.filter((item) => item.channelName !== PaymentMethodsServerKeys.SG_WIRE_TRANSFER);
	}
	let sortRule: any = undefined;

	switch (currency) {
		case "USD":
			if (Number(price) <= 1000) {
				sortRule = paymentMethodSortRules.cardBeforeWireTransferRule;
			} else {
				sortRule = paymentMethodSortRules.hkWireTransferBeforeWireTransferRule;
			}
			break;
		case "PHP":
		case "IDR":
			sortRule = paymentMethodSortRules.bankBeforeEWallet;
			break;
		case "MXN":
		case "BRL":
			sortRule = paymentMethodSortRules.bankBeforeCard;
			break;
		case "HKD":
			sortRule = paymentMethodSortRules.hkWireTransferBeforeWireTransfer;
		case "SGD":
			sortRule = paymentMethodSortRules.sgdFirstOther;
		case "HKD":
			sortRule = paymentMethodSortRules.hkWireTransferBeforeCardRule;
			break;
		default:
			break;
	}

	if (sortRule !== undefined) {
		_data = _data.sort((pre, next) => sortRule.indexOf(pre.channelName) - sortRule.indexOf(next.channelName));
	}

	// dcs 排在最后 其他的位置不变 support 排在最后
	_data = _data
		.sort((pre, next) => (next.companyCode === CompanyCodes.DCS ? -1 : 0))
		.sort((pre, next) => (next.support ? 1 : 0) - (pre.support ? 1 : 0));

	return [..._data];
}

export default function RapydPayment({
	visibleCount,
	orderId,
	channelData,
	_currency,
	_price,
	_region,
}: RapydPaymentProps) {
	const { t } = useTranslation(["rapyd", "error"]);
	const router = useRouter();

	// 上下参数
	const depositInfo = useContext(DepositContext);
	// 用于请求接口
	const [region, setRegion] = useState<string>(_region);
	// 接口返回 用于显示

	const [paymentMethods, setPaymentMethods] = useState<ResponseChannel[]>(() =>
		transferChannelData(channelData, _currency, _price),
	);
	const [currency, setCurrency] = useState(_currency);
	const [price, setPrice] = useState(_price);
	const [loading, setLoading] = useState(false);
	const [ready, setReady] = useState(true);
	const [limitVisible, setLimitVisible] = useState(false);

	// 如果服务端渲染的数据为空 -- 客户端请求
	useEffect(() => {
		if (channelData.length > 0) return;
		getPaymentMethodsFromServer();
	}, []);

	useEffect(() => {
		if (!depositInfo) return;
		const { price, currency, defaultRegion } = depositInfo;
		if (_price !== price || _currency !== currency) {
			setPrice(() => price);
			setCurrency(() => currency);
			setRegion(() => defaultRegion);
			getPaymentMethodsFromServer();
		}
	}, [depositInfo]);

	useEffect(() => {
		// 支付失败回来
		if (!visibleCount) return;
		paymentErrorMsg();
	}, []);

	const paymentErrorMsg = () => {
		if (!orderId) {
			Toast(t("payment-callback-msg"));
			return;
		}

		paymentErrorCode();
	};

	const paymentErrorCode = async () => {
		try {
			const data = await getPaymentErrorInfos(orderId || "");

			if (tryErrorCount >= 3 || data.errorCode) {
				const isExistErrCode = i18n?.exists(`error:${data.errorCode}`);
				const msg = isExistErrCode ? t(data.errorCode, { ns: "error" }) : t("payment-callback-msg");

				Toast(msg);
			} else {
				tryErrorCount++;
				await new Promise((resolve) => setTimeout(resolve, 1000));
				await paymentErrorCode();
			}
		} catch (error) {
			// showMessage(t("payment-callback-msg"));
		}
	};

	const getPaymentMethodsFromServer = async () => {
		setReady(false);

		const rfState = rfStore.getRFState();

		// FIXME: android 会出现没有region的情况 导致接口出错
		if ((rfState && !rfState?.region) || rfState?.region === "--") {
			rfStore.setRFState({
				...rfState,
				region: depositInfo?.defaultRegion || "CN",
			});
		}

		try {
			const data = await getPaymentChannels(currency);
			if (!data || data.length === 0) throw new Error("No payment channel Support");

			const _data = transferChannelData(data, currency, price);
			setPaymentMethods(_data);

			if (_data.every((item) => !item.support)) {
				log2Sentry(new DepositError("No payment channel Support"), "error", {
					...rfStore.getRFState(),
					res: _data,
				});
			}
		} catch (error: any) {
			log2Sentry(new DepositError("No payment channel data"), "error", {
				...rfStore.getRFState(),
			});
		} finally {
			setReady(true);
		}
	};

	const handleCardClick = async (item?: ResponseChannel, title?: string) => {
		// 是否支持
		if (!item || !item.support) return;

		// 转账
		const isTransfer =
			!item.channelName ||
			item.channelName === PaymentMethodsServerKeys.WIRE_TRANSFER ||
			item.channelName === PaymentMethodsServerKeys.SG_WIRE_TRANSFER ||
			item.channelName === PaymentMethodsServerKeys.HK_WIRE_TRANSFER;

		if (isTransfer) {
			if (currency === "USD" && Number(price) < 100) {
				setLimitVisible(true);
				return;
			}

			router.push(`/rapyd/bankAccount?currency=${currency}&amount=${price}&channelName=${item.channelName}`);
			return;
		}

		// checkout
		if (item.channelName === PaymentMethodsServerKeys.CARD) {
			handleCheckCredit(item.channelCode, item.companyCode);
			return;
		}

		// 电子钱包【dukpay、rapyd】 有下一级渠道
		if (item.multistage) {
			router.push(
				`/rapyd/${item.channelCode}?region=${region}&currency=${depositInfo?.currency}&channelName=${item.channelName}`,
			);
			return;
		}

		// 没有下级渠道直接支付
		setLoading(true);
		const params: OrderParams = {
			currency: currency,
			price: price,
			channelId: 1,
			companyCode: item.companyCode,
			channelCode: item.channelCode,
			ext: {
				completePaymentUrl: getCompletePaymentUrl(item.channelName, item.companyCode),
				errorPaymentUrl: getErrorPaymentUrl(item.channelName),
				paymentMethodCode: item.paymentMethodCode,
				email: depositInfo?.email,
			},
		};

		try {
			const data = await createOrder(params);
			if (!data || !data.ext.redirectUrl) {
				console.log("Error creating order redirectUrl is miss");
				return;
			}

			handleDeepLink(clientDeepLinks.webview(data?.ext.redirectUrl, ClientHeadBtnTypes.customerservice));
		} catch (error) {
			Toast.fail(error as any);
		} finally {
			setLoading(false);
		}
	};

	const handleCheckCredit = async (channelCode: string, companyCode: string) => {
		try {
			setLoading(true);
			// router.push(`/rapyd/dcs?channelCode=${item?.channelCode}&region=${region}&sourceFrom=rapyd`);
			const cards = await getCreditCards(companyCode);
			let path = "";
			if (cards.length > 0) {
				path =
					companyCode === CompanyCodes.CHECKOUT
						? `/rapyd/creditCardList?channelCode=${channelCode}&region=${region}`
						: `/rapyd/dcsCardList?channelCode=${channelCode}&region=${region}`;
			} else {
				path =
					companyCode === CompanyCodes.CHECKOUT
						? `/rapyd/creditCard?channelCode=${channelCode}&region=${region}&sourceFrom=rapyd`
						: `/rapyd/dcs?channelCode=${channelCode}&region=${region}&sourceFrom=rapyd`;
			}

			router.push(path);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const getCompletePaymentUrl = (channelName: string, companyCode: string) => {
		const domain = getDomain();
		// 特殊处理dukpay
		if (companyCode === CompanyCodes.DUKPAY) {
			return `${domain}/rapyd/middleware?region=${region}&channelName=${channelName}&currency=${currency}&price=${price}`;
		}
		return `${domain}/rapyd/success?region=${region}&channelName=${channelName}&currency=${currency}&price=${price}`;
	};

	const getErrorPaymentUrl = (channelName: string) => {
		const domain = getDomain();
		const visibleCount = router.query.visibleCount ? Number(router.query.visibleCount) + 1 : 1;
		return `${domain}/rapyd?region=${region}&channelName=${channelName}&visibleCount=${visibleCount}`;
	};

	const getDomain = () => {
		if (typeof window === "undefined") {
			return "";
		}

		const localePrefix = router.locale === "en" ? "" : `/${router.locale}`;
		return window.location.origin + localePrefix;
	};

	const showRecommend = (channelName: string, support: boolean) => {
		return support && channelName === PaymentMethodsServerKeys.HK_WIRE_TRANSFER;
	};

	const renderCardContent = () => {
		if (!ready || paymentMethods.length === 0) {
			return new Array(4).fill(1).map((i, k) => {
				return <PaymentCard key={k} />;
			});
		}

		return paymentMethods.map((item, key) => (
			<PaymentCard
				key={key}
				info={item}
				currency={currency}
				onCardClick={handleCardClick}
				showRecommend={showRecommend(item.channelName, item.support)}
			/>
		));
	};

	return (
		<DepositContainer
			titleHeader={{
				title: t("deposit-method"),
			}}
		>
			<div className="mt-5 pl-4.5">{renderCardContent()}</div>
			<div className="px-4.5">
				<ServiceProvider />
				{loading && <LoadingWidget />}

				<NoticeModalBottom
					title={t("deposit-amount-too-low")}
					content={t("the-deposit-amount-by-wire-transfer", { amount: 100, currency: "USD" })}
					visible={limitVisible}
					onClose={() => setLimitVisible(false)}
					okText={t("change-amount")}
					onOk={() => {
						setLimitVisible(false);
					}}
				/>
			</div>
		</DepositContainer>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { region, currency, price, visibleCount, orderId } = context.query;
	const rfState = rfStore.getRFState();
	if (rfState) {
		rfStore.setRFState({
			...rfState,
			region: (region as string) || "CN",
		});
	}

	let channelData: ResponseChannel[] = [];

	try {
		const data = await getPaymentChannels((currency as string) || "USD");

		channelData = JSON.parse(JSON.stringify(data || []));
	} catch (error) {
		log2Sentry(new DepositError("No payment channel Support"), "error", {
			...rfStore.getRFState(),
			res: error,
		});
	}

	return {
		props: {
			...context.query,
			visibleCount: visibleCount || "",
			orderId: orderId || "",
			channelData,
			_currency: currency,
			_price: price,
			_region: region,
			...(await serverSideTranslations(context.locale as string, ["error", "rapyd", "common"])),
		},
	};
};

RapydPayment.getLayout = function getLayout(page: ReactElement) {
	return <DepositLayout>{page}</DepositLayout>;
};
